/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType, PropsWithRef } from "react";
import { Base } from "./base";
import { HOC } from "./types";

/**
 * A builder class for composing a specific React component using HOCs.
 * Wraps the Base class and provides a fluent API for enhancing the component.
 */
export class ComponentBuilderEquipped<TAccumulatedProps = object> {
  private Component: ComponentType<TAccumulatedProps>;
  private baseBuilder: Base<TAccumulatedProps>;

  /**
   * Initializes the builder with a target component and optional list of HOCs.
   * @param Component - The React component to enhance.
   * @param hocs - Initial array of HOCs to apply.
   */
  constructor(Component: ComponentType<TAccumulatedProps>, hocs: HOC<any>[] = []) {
    this.Component = Component;
    this.baseBuilder = new Base<TAccumulatedProps>(ComponentBuilderEquipped.name);
    this.baseBuilder.setHocs = hocs;
  }

  /**
   * Adds a new HOC to the builder and returns a new instance with updated accumulated props.
   * @param hoc - A higher-order component to apply.
   * @returns A new builder instance with extended prop types.
   */
  compose<TNewProps>(hoc: HOC<TNewProps>): ComponentBuilderEquipped<TAccumulatedProps & TNewProps> {
    return new ComponentBuilderEquipped<TAccumulatedProps & TNewProps>(
      this.Component as any,
      [...this.baseBuilder.getHocs, hoc]
    );
  }

  /**
   * Adds a parameterized HOC to the builder using a factory function and its arguments.
   * @param hocWithArgs - A function that returns an HOC when called with a component and arguments.
   * @param args - Arguments to pass to the HOC factory.
   * @returns A new builder instance with extended prop types.
   */
  composeWithArgs<
    P,
    H extends <C extends ComponentType<any>>(component: C, a: P) => ComponentType<any>,
  >(
    hocWithArgs: H,
    args: P
  ) {
    const hoc = this.baseBuilder.composeWithArgsFactory(hocWithArgs, args);
    return this.compose(hoc);
  }

  /**
   * Builds the final enhanced component by applying all composed HOCs.
   * @returns A new component with merged props.
   */
  build() {
    return this.baseBuilder.build(this.Component);
  }

  /**
   * Builds the final enhanced component with some props pre-injected.
   * @param injectedProps - A subset of props to inject automatically.
   * @returns A component with the remaining props required.
   */
  buildWithProps<K extends keyof TAccumulatedProps>(
    injectedProps: Pick<PropsWithRef<TAccumulatedProps> & TAccumulatedProps, K>
  ) {
    return this.baseBuilder.buildWithProps(this.Component, injectedProps);
  }
}
